import { useState } from "react";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getInvoice,
  listInvoiceLineItems,
  getInvoiceTransactions,
  issueInvoice,
  cancelInvoice,
  sendInvoiceReminder,
  getInvoicePdfUrl,
  listPayments,
  getPayment,
  reversePayment,
  listInvoiceEmailLogs,
  type InvoiceDto,
  type InvoiceLineItemDto,
  type InvoiceTransactionMappingDto,
  type PaymentRecordSummaryDto,
  type EmailLogDto,
} from "@/lib/invoices-payments";
import {
  InvoiceStatusBadge,
  InvoicePdfFrame,
  InvoiceTotalsTable,
  type InvoiceStatusCode,
} from "@/components/invoices-payments";
import { PaymentMethodBadge } from "@/components/payment-method-badge";
import { useAuth } from "@/app/auth-context";
import { DetailPage } from "@/archetypes/DetailPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { RecordPaymentModal } from "@/pages/payments/RecordPaymentModal";

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

function OverviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function OverviewTab({ invoice }: { invoice: InvoiceDto }) {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <OverviewRow label="Issue date" value={invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-AU") : "—"} />
        <OverviewRow label="Due date"   value={invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-AU") : "—"} />
        <OverviewRow label="Payment terms" value="—" />
        <OverviewRow label="Notes" value={invoice.commentToShowOnInvoice ?? "—"} />
        <OverviewRow label="Linked job" value={invoice.jobId != null ? `#${invoice.jobId}` : "—"} />
        <OverviewRow label="Originating quote" value="—" />
        <OverviewRow label="Issued at" value={invoice.issuedAtUtc ? new Date(invoice.issuedAtUtc).toLocaleString("en-AU") : "—"} />
        <OverviewRow label="Reminder issued" value={invoice.reminderIssuedOn ? new Date(invoice.reminderIssuedOn).toLocaleString("en-AU") : "—"} />
        <OverviewRow label="Paid on" value={invoice.paidOn ? new Date(invoice.paidOn).toLocaleString("en-AU") : "—"} />
        <OverviewRow label="MYOB exported" value={invoice.myobExportedAtUtc ? new Date(invoice.myobExportedAtUtc).toLocaleString("en-AU") : "—"} />
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line items tab
// ---------------------------------------------------------------------------

function LineItemsTab({ invoice }: { invoice: InvoiceDto }) {
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-line-items", invoice.invoiceId],
    queryFn: () => listInvoiceLineItems(invoice.invoiceId),
  });

  const cols: DataGridColumn<InvoiceLineItemDto>[] = [
    { id: "desc", header: "Description", accessor: (r) => r.description },
    { id: "qty",  header: "Qty",         accessor: (r) => r.quantity, align: "right" },
    { id: "unit", header: "Unit (ex GST)", accessor: (r) => fmt.format(r.unitPriceExGst), align: "right" },
    { id: "disc", header: "Disc %",      accessor: (r) => `${r.discountPct}`, align: "right" },
    { id: "total", header: "Total (ex GST)", accessor: (r) => fmt.format(r.totalExGst), align: "right" },
    { id: "gst",  header: "GST?",        accessor: (r) => (r.gstApplicable ? "✓" : "—"), align: "center" },
    { id: "credit", header: "Credit?",   accessor: (r) => (r.isCredit ? "✓" : "—"), align: "center" },
  ];

  return (
    <div className="space-y-4">
      <DataGrid
        rows={data ?? []}
        columns={cols}
        rowKey={(r) => r.lineItemId}
        totalRows={(data ?? []).length}
        pageIndex={0}
        pageSize={1000}
        loading={isLoading}
        emptyHeadline="No line items."
        onPageChange={() => {}}
      />
      <div className="flex justify-end">
        <InvoiceTotalsTable
          subtotalExGst={invoice.invoiceTotalExGst}
          gst={invoice.gst}
          totalIncGst={invoice.invoiceTotalIncGst}
          balanceOutstanding={invoice.balanceOutstanding}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transactions tab
// ---------------------------------------------------------------------------

function TransactionsTab({ invoiceId }: { invoiceId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-transactions", invoiceId],
    queryFn: () => getInvoiceTransactions(invoiceId),
  });

  const cols: DataGridColumn<InvoiceTransactionMappingDto>[] = [
    { id: "date", header: "Date", accessor: (r) => r.createdOn ? new Date(r.createdOn).toLocaleString("en-AU") : "—" },
    { id: "tran", header: "Tran ID", accessor: (r) => r.tranId ?? "—" },
    { id: "amount", header: "Amount", accessor: (r) => fmt.format(r.amount), align: "right" },
    { id: "note", header: "Note", accessor: (r) => r.note ?? "—" },
  ];

  return (
    <DataGrid
      rows={data ?? []}
      columns={cols}
      rowKey={(r) => r.invoiceTransactionMapId}
      totalRows={(data ?? []).length}
      pageIndex={0}
      pageSize={1000}
      loading={isLoading}
      emptyHeadline="No transactions."
      onPageChange={() => {}}
    />
  );
}

// ---------------------------------------------------------------------------
// Payments tab
// ---------------------------------------------------------------------------

function ReversePaymentDialog({
  open,
  onOpenChange,
  payment,
  onReversed,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  payment: PaymentRecordSummaryDto;
  onReversed: () => void;
}) {
  const [reason, setReason] = useState("");

  // Fetch full payment to get the rowVersion needed for optimistic concurrency.
  const { data: full, isLoading: loadingFull } = useQuery({
    queryKey: ["payment", payment.paymentRecordId],
    queryFn: () => getPayment(payment.paymentRecordId),
    enabled: open,
  });

  const reverseMut = useMutation({
    mutationFn: () =>
      reversePayment(payment.paymentRecordId, {
        reason,
        rowVersion: full?.rowVersion ?? null,
      }),
    onSuccess: () => {
      toast.success("Payment reversed");
      onOpenChange(false);
      setReason("");
      onReversed();
    },
    onError: () => toast.error("Failed to reverse payment"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reverse payment</DialogTitle>
          <DialogDescription>
            {`Reversing ${fmt.format(payment.tranAmount)} payment. This action cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) reverseMut.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="reverse-reason">Reason *</Label>
            <textarea
              id="reverse-reason"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={reverseMut.isPending || loadingFull || !reason.trim()}
            >
              {reverseMut.isPending ? "Reversing…" : "Reverse payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsTab({
  invoiceId,
  onRecordPayment,
}: {
  invoiceId: number;
  onRecordPayment: () => void;
}) {
  const qc = useQueryClient();
  const { permissions } = useAuth();
  const canWrite = permissions.has("payments:write");

  const [reversingPayment, setReversingPayment] = useState<PaymentRecordSummaryDto | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["invoice-payments", invoiceId],
    queryFn: () => listPayments({ invoiceId }),
  });

  const rows = data?.items ?? [];

  const cols: DataGridColumn<PaymentRecordSummaryDto>[] = [
    {
      id: "method",
      header: "Method",
      accessor: (r) => (
        <PaymentMethodBadge code={r.paymentTypeCode} />
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessor: (r) => fmt.format(r.tranAmount),
      align: "right",
    },
    {
      id: "date",
      header: "Date",
      accessor: (r) => new Date(r.transactionDate).toLocaleDateString("en-AU"),
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => r.status,
    },
    {
      id: "actions",
      header: "",
      accessor: (r) =>
        canWrite && r.status === "Posted" ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setReversingPayment(r)}
          >
            Reverse
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={onRecordPayment}>Record Payment</Button>
      </div>
      <DataGrid
        rows={rows}
        columns={cols}
        rowKey={(r) => r.paymentRecordId}
        totalRows={rows.length}
        pageIndex={0}
        pageSize={1000}
        loading={isLoading}
        emptyHeadline="No payments recorded."
        onPageChange={() => {}}
      />
      {reversingPayment && (
        <ReversePaymentDialog
          open={reversingPayment !== null}
          onOpenChange={(o) => { if (!o) setReversingPayment(null); }}
          payment={reversingPayment}
          onReversed={() => {
            void qc.invalidateQueries({ queryKey: ["invoice-payments", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Emails tab
// ---------------------------------------------------------------------------

function EmailsTab({ invoiceId }: { invoiceId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-email-logs", invoiceId],
    queryFn: () => listInvoiceEmailLogs(invoiceId),
  });

  const cols: DataGridColumn<EmailLogDto>[] = [
    { id: "kind",    header: "Kind",      accessor: (r) => r.kind },
    { id: "to",      header: "To",        accessor: (r) => r.toEmailAddress },
    { id: "subject", header: "Subject",   accessor: (r) => r.subject },
    {
      id: "sentAt",
      header: "Sent at",
      accessor: (r) => new Date(r.sentAtUtc).toLocaleString("en-AU"),
    },
    {
      id: "ok",
      header: "Sent?",
      accessor: (r) => (r.sentSuccessfully ? "✓" : "✗"),
      align: "center",
    },
  ];

  return (
    <DataGrid
      rows={data ?? []}
      columns={cols}
      rowKey={(r) => r.emailLogId}
      totalRows={(data ?? []).length}
      pageIndex={0}
      pageSize={1000}
      loading={isLoading}
      emptyHeadline="No emails sent for this invoice."
      onPageChange={() => {}}
    />
  );
}

// ---------------------------------------------------------------------------
// Cancel modal
// ---------------------------------------------------------------------------

function CancelInvoiceDialog({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  invoice: InvoiceDto;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");

  const cancelMut = useMutation({
    mutationFn: () => cancelInvoice(invoice.invoiceId, { reason, rowVersion: invoice.rowVersion }),
    onSuccess: () => {
      toast.success("Invoice cancelled");
      void qc.invalidateQueries({ queryKey: ["invoice", invoice.invoiceId] });
      onOpenChange(false);
      setReason("");
    },
    onError: () => toast.error("Failed to cancel invoice"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel invoice</DialogTitle>
          <DialogDescription>
            Provide a reason. This action will mark the invoice as Cancelled.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); if (reason.trim()) cancelMut.mutate(); }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="cancel-reason">Reason *</Label>
            <Input
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button type="submit" variant="destructive" disabled={cancelMut.isPending || !reason.trim()}>
              {cancelMut.isPending ? "Cancelling…" : "Cancel invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Issue confirmation dialog
// ---------------------------------------------------------------------------

function IssueInvoiceDialog({
  open,
  onOpenChange,
  invoice,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  invoice: InvoiceDto;
}) {
  const qc = useQueryClient();
  const issueMut = useMutation({
    mutationFn: () => issueInvoice(invoice.invoiceId, { rowVersion: invoice.rowVersion }),
    onSuccess: () => {
      toast.success("Invoice issued");
      void qc.invalidateQueries({ queryKey: ["invoice", invoice.invoiceId] });
      onOpenChange(false);
    },
    onError: () => toast.error("Failed to issue invoice"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue invoice</DialogTitle>
          <DialogDescription>
            This finalises the invoice ({invoice.invoiceReferenceNo}) and marks it as Sent.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Back</Button>
          <Button onClick={() => issueMut.mutate()} disabled={issueMut.isPending}>
            {issueMut.isPending ? "Issuing…" : "Issue invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Primary action bar
// ---------------------------------------------------------------------------

function InvoiceActions({ invoice }: { invoice: InvoiceDto }) {
  const status = invoice.invoiceStatusCode as InvoiceStatusCode;
  const [issueOpen, setIssueOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const reminderMut = useMutation({
    mutationFn: () => sendInvoiceReminder(invoice.invoiceId, { customMessage: null }),
    onSuccess: () => toast.success("Reminder sent"),
    onError: () => toast.error("Failed to send reminder"),
  });

  const canCancel = ["Sent", "PartPaid", "Paid", "OverPaid", "Overdue"].includes(status);

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-2">
        {/* Status-driven primary action */}
        {status === "Draft" && (
          <Button onClick={() => setIssueOpen(true)}>Issue</Button>
        )}

        {(status === "Sent" || status === "PartPaid" || status === "Overdue") && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button disabled>Record Payment</Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Coming in Plan II</TooltipContent>
            </Tooltip>
            <Button
              variant="outline"
              onClick={() => reminderMut.mutate()}
              disabled={reminderMut.isPending}
            >
              {reminderMut.isPending ? "Sending…" : "Send Reminder"}
            </Button>
          </>
        )}

        {(status === "Paid" || status === "OverPaid") && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button disabled>View Receipt</Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Coming in Plan II</TooltipContent>
          </Tooltip>
        )}

        {status === "Cancelled" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button disabled>Duplicate to new draft</Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Coming in a future plan</TooltipContent>
          </Tooltip>
        )}

        {/* Always-available secondary actions */}
        {canCancel && (
          <Button variant="outline" onClick={() => setCancelOpen(true)}>
            Cancel
          </Button>
        )}
        <a href={getInvoicePdfUrl(invoice.invoiceId)} download>
          <Button variant="outline" type="button">Download PDF</Button>
        </a>

        <IssueInvoiceDialog open={issueOpen} onOpenChange={setIssueOpen} invoice={invoice} />
        <CancelInvoiceDialog open={cancelOpen} onOpenChange={setCancelOpen} invoice={invoice} />
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// InvoiceDetailPage
// ---------------------------------------------------------------------------

export function InvoiceDetailPage() {
  const { id = "0" } = useParams();
  const invoiceId = Number(id);
  const qc = useQueryClient();

  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);

  const { data: invoice } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => getInvoice(invoiceId),
  });

  const status = invoice?.invoiceStatusCode as InvoiceStatusCode | undefined;

  const tabs = invoice
    ? [
        { id: "overview",     label: "Overview",     content: <OverviewTab invoice={invoice} /> },
        { id: "lineItems",    label: "Line items",   content: <LineItemsTab invoice={invoice} /> },
        {
          id: "payments",
          label: "Payments",
          content: (
            <PaymentsTab
              invoiceId={invoiceId}
              onRecordPayment={() => setRecordPaymentOpen(true)}
            />
          ),
        },
        {
          id: "transactions",
          label: "Transactions",
          content: <TransactionsTab invoiceId={invoiceId} />,
        },
        {
          id: "emails",
          label: "Emails",
          content: <EmailsTab invoiceId={invoiceId} />,
        },
        {
          id: "files",
          label: "Files",
          content: (
            <p className="text-center text-sm text-muted-foreground py-8">
              PDF blob preview is in the right pane.
            </p>
          ),
        },
        {
          id: "audit",
          label: "Audit",
          content: (
            <p className="text-center text-sm text-muted-foreground py-8">
              Audit history available in admin views.
            </p>
          ),
        },
      ]
    : [];

  const headerTitle = invoice ? (
    <span className="flex flex-wrap items-center gap-3">
      <span>{invoice.invoiceReferenceNo}</span>
      <span className="text-sm text-muted-foreground">— Client #{invoice.clientId ?? "—"}</span>
      <span className="text-sm text-muted-foreground">· Account #{invoice.accountId ?? "—"}</span>
      {status ? <InvoiceStatusBadge code={status} /> : null}
      <span className="text-sm">Total: {fmt.format(invoice.invoiceTotalIncGst)}</span>
      <span className="text-sm">Balance: {fmt.format(invoice.balanceOutstanding)}</span>
    </span>
  ) : (
    "Loading…"
  );

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_500px]">
        <DetailPage
          title={headerTitle}
          primaryAction={invoice ? <InvoiceActions invoice={invoice} /> : undefined}
          tabs={tabs}
          defaultTab="overview"
        />
        {invoice ? (
          <div className="px-6">
            <InvoicePdfFrame invoiceId={invoice.invoiceId} />
          </div>
        ) : null}
      </div>

      {invoice && (
        <RecordPaymentModal
          open={recordPaymentOpen}
          onOpenChange={setRecordPaymentOpen}
          invoiceId={invoiceId}
          accountId={invoice.accountId ?? undefined}
          defaultAmount={invoice.balanceOutstanding}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: ["invoice-payments", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
            void qc.invalidateQueries({ queryKey: ["payments"] });
          }}
        />
      )}
    </>
  );
}
